// Модальные слои: окно (Modal) и шторка справа (Drawer). Esc закрывает только верхний слой.
import { useEffect, useId, useRef, type ReactNode, type RefObject } from 'react'
import { IconButton, cx } from './ui.tsx'

const stack: number[] = []
let layerSeq = 0

function useLayer(onClose: () => void, panel: RefObject<HTMLElement | null>): void {
  const closeRef = useRef(onClose)
  useEffect(() => {
    closeRef.current = onClose
  })
  useEffect(() => {
    const id = ++layerSeq
    stack.push(id)
    const prevFocus = document.activeElement as HTMLElement | null
    const el = panel.current
    // фокус внутрь: первое поле ввода или сама панель
    const first = el?.querySelector<HTMLElement>('[data-autofocus], input:not([type=hidden]), textarea, select')
    ;(first ?? el)?.focus({ preventScroll: true })

    const onKey = (e: KeyboardEvent) => {
      if (stack[stack.length - 1] !== id) return
      if (e.key === 'Escape') {
        e.preventDefault()
        closeRef.current()
        return
      }
      if (e.key === 'Tab' && el) {
        const items = Array.from(
          el.querySelectorAll<HTMLElement>(
            'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
          ),
        ).filter((n) => n.offsetParent !== null)
        if (!items.length) return
        const firstEl = items[0]!
        const lastEl = items[items.length - 1]!
        if (e.shiftKey && document.activeElement === firstEl) {
          e.preventDefault()
          lastEl.focus()
        } else if (!e.shiftKey && document.activeElement === lastEl) {
          e.preventDefault()
          firstEl.focus()
        }
      }
    }
    document.addEventListener('keydown', onKey)
    const prevOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.removeEventListener('keydown', onKey)
      const i = stack.indexOf(id)
      if (i !== -1) stack.splice(i, 1)
      if (!stack.length) document.body.style.overflow = prevOverflow
      if (prevFocus && document.contains(prevFocus)) prevFocus.focus({ preventScroll: true })
    }
  }, [panel])
}

export function Modal({
  title,
  onClose,
  children,
  footer,
  width = 'max-w-xl',
}: {
  title: ReactNode
  onClose: () => void
  children: ReactNode
  footer?: ReactNode
  width?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  const titleId = useId()
  useLayer(onClose, ref)
  return (
    // на телефоне окно — не шире экрана и не выше его: шапка с крестиком и низ с кнопками на месте, прокручивается середина
    <div
      className="backdrop fixed inset-0 z-50 flex items-start justify-center overflow-y-auto p-4 pt-[8vh] max-md:p-3 max-md:pt-3"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={cx(
          'anim-fade-in w-full min-w-0 rounded-panel border border-line bg-surface shadow-float outline-none max-md:flex max-md:max-h-full max-md:flex-col',
          width,
        )}
      >
        <div className="flex shrink-0 items-center justify-between gap-3 border-b border-line px-5 py-3.5 max-md:px-4 max-md:py-3">
          <h2 id={titleId} className="min-w-0 text-[16px] font-[650] tracking-[-0.01em] [overflow-wrap:anywhere]">
            {title}
          </h2>
          <IconButton icon="x" label="Закрыть" onClick={onClose} />
        </div>
        <div className="min-w-0 px-5 py-4 max-md:min-h-0 max-md:flex-1 max-md:overflow-y-auto max-md:overscroll-contain max-md:px-4">{children}</div>
        {footer ? (
          <div className="flex shrink-0 flex-wrap items-center justify-end gap-2 rounded-b-panel border-t border-line bg-surface-2 px-5 py-3 max-md:px-4">
            {footer}
          </div>
        ) : null}
      </div>
    </div>
  )
}

export function Drawer({
  onClose,
  children,
  label,
  width = 'w-[min(920px,100vw)]',
}: {
  onClose: () => void
  children: ReactNode
  label: string
  width?: string
}) {
  const ref = useRef<HTMLDivElement>(null)
  useLayer(onClose, ref)
  return (
    <div
      className="backdrop fixed inset-0 z-40 flex justify-end"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-label={label}
        tabIndex={-1}
        // min-w-0/max-w-full: широкое содержимое не раздвигает шторку за край экрана; на телефоне — во всю ширину
        className={cx(
          'anim-slide-in flex h-full min-w-0 max-w-full flex-col overflow-x-clip border-l border-line bg-bg shadow-float outline-none max-md:w-full max-md:border-l-0',
          width,
        )}
      >
        {children}
      </div>
    </div>
  )
}
